/**
 * Safe metrics service that sanitizes data before sending to the server
 * This ensures no sensitive parameters are included in metrics tracking
 * Includes memory optimization with debouncing, batching, and error handling
 */
import axios from 'axios';

// Configuration
const BATCH_INTERVAL = 2000; // 2 seconds between batches
const MAX_QUEUE_SIZE = 20; // Maximum number of events to queue before forcing a send
const MAX_RETRY_ATTEMPTS = 3; // Maximum number of retry attempts for failed requests

const safeMetrics = {
  // Queue to store metrics before sending in batch
  _queue: [],

  // Timer for batching
  _batchTimer: null,

  // Track if service is active
  _isActive: true,

  // Debounce timers by event type
  _debounceTimers: {},

  /**
   * Track a metric event with sanitized data
   * @param {Object} data - The metric data
   * @param {string} data.type - The type of metric (click, view, etc.)
   * @param {string} data.key - The key identifier for the metric
   * @param {boolean} [data.immediate=false] - Whether to send immediately or batch
   * @param {boolean} [data.preserveKey=false] - Whether to preserve the full key without truncation
   * @returns {Promise|undefined} - Returns a promise if immediate is true
   */
  track(data) {
    // Don't process if service is not active
    if (!this._isActive) return;

    // Only send the type and a sanitized key (no dynamic user input)
    const sanitizedData = {
      type: data.type,
      // Use full key if preserveKey is true, otherwise truncate for safety
      key: data.preserveKey ? data.key : data.key.split('_').slice(0, 2).join('_'),
      timestamp: Date.now()
    };

    // For page_view events, strip query parameters from the path
    if (data.type === 'page_view' && data.key.startsWith('/')) {
      // Remove query parameters (anything after ? or #)
      sanitizedData.key = data.key.split(/[?#]/)[0];
    }


    // Debounce certain event types (like page_view) to prevent excessive tracking
    if (data.type === 'page_view' || data.type === 'scroll') {
      const debounceKey = `${data.type}_${sanitizedData.key}`;

      // Clear existing timer for this event type
      if (this._debounceTimers[debounceKey]) {
        clearTimeout(this._debounceTimers[debounceKey]);
      }


      // Set new debounce timer
      this._debounceTimers[debounceKey] = setTimeout(() => {
        // Add to queue or send immediately
        if (data.immediate) {
          return this._sendMetric(sanitizedData);
        } else {
          this._addToQueue(sanitizedData);
        }
        delete this._debounceTimers[debounceKey];
      }, 300); // 300ms debounce

      return;
    }

    // For non-debounced events
    if (data.immediate) {
      return this._sendMetric(sanitizedData);
    } else {
      this._addToQueue(sanitizedData);
    }
  },

  /**
   * Add a metric to the queue and schedule batch sending
   * @private
   * @param {Object} data - The sanitized metric data
   */
  _addToQueue(data) {
    // Add to queue
    this._queue.push(data);


    // If queue reaches max size, send immediately
    if (this._queue.length >= MAX_QUEUE_SIZE) {
      this._sendBatch();
      return;
    }

    // Schedule batch sending if not already scheduled
    if (!this._batchTimer) {
      this._batchTimer = setTimeout(() => {
        this._sendBatch();
      }, BATCH_INTERVAL);
    }
  },

  /**
   * Send a single metric immediately
   * @private
   * @param {Object} data - The sanitized metric data
   * @param {number} [attempt=0] - Current attempt number for retries
   * @returns {Promise} - Axios promise
   */
  _sendMetric(data, attempt = 0) {
    return axios.post('/api/metrics/track', data)
      .catch(error => {
        console.error('Error sending metric:', error);

        // Retry with exponential backoff if not exceeded max attempts
        if (attempt < MAX_RETRY_ATTEMPTS && this._isActive) {
          const backoffTime = Math.pow(2, attempt) * 1000; // Exponential backoff

          return new Promise(resolve => {
            setTimeout(() => {
              resolve(this._sendMetric(data, attempt + 1));
            }, backoffTime);
          });
        }

        // If max retries exceeded, fail silently
        return Promise.resolve();
      });
  },

  /**
   * Send all queued metrics in a batch
   * @private
   */
  _sendBatch() {
    // Clear the timer
    clearTimeout(this._batchTimer);
    this._batchTimer = null;

    // If queue is empty or service is inactive, do nothing
    if (this._queue.length === 0 || !this._isActive) return;

    // Get current queue and reset
    const batch = [...this._queue];
    this._queue = [];

    // Since there's no batch endpoint, we'll process each item individually
    // but we'll do it sequentially to avoid overwhelming the server
    const processMetrics = async () => {
      if (window.location.hostname === 'localhost') {
        return;
      }
      // Only process a limited number of metrics at a time to avoid memory issues
      const metricsToProcess = batch.slice(0, 10);

      // Process each metric sequentially
      for (const metric of metricsToProcess) {
        if (!this._isActive) break; // Stop if service was deactivated

        try {
          await axios.post('/api/metrics/track', metric);
        } catch (error) {
          console.error('Error sending metric:', error);
          // Don't retry individual metrics to avoid loops
        }

        // Small delay between requests to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // If there are more metrics to process and service is still active, schedule next batch
      if (batch.length > 10 && this._isActive) {
        batch.splice(0, 10); // Remove processed metrics
        setTimeout(() => {
          // If service is still active, continue processing
          if (this._isActive) {
            // Re-add remaining metrics to the queue
            this._queue = [...batch, ...this._queue];
            this._sendBatch();
          }
        }, 500); // Small delay before processing next batch
      }
    };

    // Start processing metrics
    processMetrics().catch(error => {
      console.error('Error in batch processing:', error);

      // On catastrophic failure, re-add some items to queue if service is still active
      if (this._isActive) {
        // Only add back the most recent metrics to prevent queue from growing too large
        const recentMetrics = batch.slice(-MAX_QUEUE_SIZE/2);
        this._queue = [...recentMetrics, ...this._queue];

        // Schedule another attempt
        this._batchTimer = setTimeout(() => {
          this._sendBatch();
        }, BATCH_INTERVAL);
      }
    });
  },

  /**
   * Clean up resources used by the metrics service
   * Call this when the application is shutting down or when metrics are no longer needed
   */
  cleanup() {
    // Mark service as inactive
    this._isActive = false;

    // Clear batch timer
    if (this._batchTimer) {
      clearTimeout(this._batchTimer);
      this._batchTimer = null;
    }

    // Clear all debounce timers
    Object.keys(this._debounceTimers).forEach(key => {
      clearTimeout(this._debounceTimers[key]);
    });
    this._debounceTimers = {};

    // Send any remaining metrics in queue
    if (this._queue.length > 0) {
      const batch = [...this._queue];
      this._queue = [];

      // Process the most important metrics only (to avoid hanging on cleanup)
      const criticalMetrics = batch.slice(0, 5);

      // Fire and forget the final metrics individually
      criticalMetrics.forEach(metric => {
        axios.post('/api/metrics/track', metric)
          .catch(() => {
            // Ignore errors on cleanup
          });
      });
    }
  }
};

export default safeMetrics;
